<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { NModal, NButton, NTag } from 'naive-ui';
import { AlertTriangle, ArrowUpRight, ShieldAlert } from 'lucide-vue-next';
import type { SeatLimitExceededError } from '@/types/models';

interface Props {
  show: boolean;
  errorData?: SeatLimitExceededError | null;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  errorData: null,
});

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'upgrade'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();
const router = useRouter();

const canUpgrade = computed(() => {
  return props.errorData?.canUpgrade === true;
});

const handleClose = () => {
  emit('update:show', false);
  emit('cancel');
};

const handleUpgrade = () => {
  emit('update:show', false);
  emit('upgrade');
  router.push('/dashboard/billing');
};
</script>

<template>
  <NModal
    :show="show"
    @update:show="(val) => emit('update:show', val)"
    :mask-closable="true"
    transform-origin="center"
  >
    <div class="w-full max-w-md bg-slate-900 border border-amber-500/40 rounded-3xl p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
      <!-- Header -->
      <div class="flex items-start gap-3.5">
        <div class="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
          <AlertTriangle class="w-6 h-6" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-base font-extrabold text-slate-100">
              {{ t('capacityExceeded.title') }}
            </h3>
            <NTag size="small" type="warning" :bordered="false">
              {{ t('capacityExceeded.badge') }}
            </NTag>
          </div>
          <p class="text-xs text-slate-400 mt-1">
            {{ errorData?.message || t('capacityExceeded.defaultMsg') }}
          </p>
        </div>
      </div>

      <!-- Capacity Stats Box -->
      <div v-if="errorData" class="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2 text-xs">
        <div class="flex items-center justify-between text-slate-300">
          <span class="text-slate-400">{{ t('capacityExceeded.contractedSeats') }}</span>
          <span class="font-mono font-bold text-slate-100">{{ errorData.totalSeats }}</span>
        </div>
        <div class="flex items-center justify-between text-slate-300">
          <span class="text-slate-400">{{ t('capacityExceeded.currentlyAllocated') }}</span>
          <span class="font-mono font-bold text-amber-400">{{ errorData.allocatedSeats }}</span>
        </div>
        <div class="flex items-center justify-between text-slate-300">
          <span class="text-slate-400">{{ t('capacityExceeded.requestedAdditional') }}</span>
          <span class="font-mono font-bold text-sky-400">+{{ errorData.requestedSeats }}</span>
        </div>
        <div class="pt-2 border-t border-slate-800/80 flex items-center justify-between font-bold text-rose-400">
          <span>{{ t('capacityExceeded.overage') }}</span>
          <span class="font-mono">{{ t('capacityExceeded.seatsNeeded', { count: (errorData.allocatedSeats + errorData.requestedSeats) - errorData.totalSeats }) }}</span>
        </div>
      </div>

      <!-- Role-based advisory text -->
      <div v-if="!canUpgrade" class="p-3 rounded-xl bg-slate-950/80 border border-slate-800 text-[11px] text-slate-400 flex items-start gap-2">
        <ShieldAlert class="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
        <p>
          {{ t('capacityExceeded.coachNotice') }}
        </p>
      </div>

      <!-- Role-Aware Footer Controls -->
      <div class="flex items-center justify-end gap-3 pt-2 border-t border-slate-800">
        <!-- CANCEL BUTTON: Always rendered for both ADMIN and COACH -->
        <NButton
          secondary
          @click="handleClose"
          class="font-semibold text-xs"
        >
          {{ t('capacityExceeded.cancelBtn') }}
        </NButton>

        <!-- UPGRADE BUTTON: Rendered ONLY when canUpgrade === true (ADMIN user) -->
        <NButton
          v-if="canUpgrade"
          type="primary"
          @click="handleUpgrade"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon>
            <ArrowUpRight class="w-4 h-4" />
          </template>
          {{ t('capacityExceeded.upgradeBtn') }}
        </NButton>
      </div>
    </div>
  </NModal>
</template>
